import { Skeleton } from '@svs/ui/react/skeleton';
import type { ReactNode } from 'react';

type SkeletonChildConfig = {
  numLines?: number;
  className: string;
};

type SkeletonConfig = {
  row?: boolean;
  numLines?: number;
  className?: string;
  children?: SkeletonChildConfig[];
};

/**
 * SkeletonWrapper component to render multiple Skeletons based on the provided config.
 *
 * @param config - Array of skeleton configuration objects.
 * @param config.config - Skeleton configuration array.
 * @returns SkeletonWrapper component
 */
export function SkeletonWrapper({ config }: { config: SkeletonConfig[] }): ReactNode {
  return (
    <div>
      {Array.isArray(config) &&
        config.flatMap((cfg, cfgIndex) => {
          // Row layout: render children in a flex row
          if ('row' in cfg && cfg.row) {
            const wrapperClassName = ['flex justify-between gap-4', cfg.className].filter(Boolean).join(' ');

            return (
              <div
                // eslint-disable-next-line react/no-array-index-key
                key={`row-${cfgIndex}`}
                className={wrapperClassName}
              >
                {(cfg.children ?? []).map((child, childIndex) => {
                  const lines = child.numLines ?? 0;

                  return Array.from({ length: lines }).map((_, lineIndex) => (
                    <Skeleton
                      // eslint-disable-next-line react/no-array-index-key
                      key={`row-${cfgIndex}-${childIndex}-${lineIndex}`}
                      className={child.className}
                    />
                  ));
                })}
              </div>
            );
          }

          // Default layout
          return Array.from({ length: cfg.numLines || 0 }).map((_, lineIndex) => (
            <Skeleton
              // eslint-disable-next-line react/no-array-index-key
              key={`${cfgIndex}-${lineIndex}`}
              className={cfg.className}
            />
          ));
        })}
    </div>
  );
}
